import { Star } from "lucide-react"
import {
  Bar,
  BarChart,
  Cell,
  LabelList,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts"

import {
  type CustomerGroupData,
  getLongTermInsight,
} from "@/helpers/company/customer-group-metrics"

function CustomLabel(
  props: { data: CustomerGroupData[] } & Record<string, unknown>
) {
  const { data } = props
  const x = typeof props.x === "number" ? props.x : undefined
  const y = typeof props.y === "number" ? props.y : undefined
  const width = typeof props.width === "number" ? props.width : undefined
  const value = typeof props.value === "string" ? props.value : undefined

  if (x === undefined || y === undefined || width === undefined) return null

  const index = typeof props.index === "number" ? props.index : undefined
  if (index === undefined) return null
  const item = data[index]
  if (!item) return null

  const centerX = x + width / 2

  return (
    <g>
      {item.hasInsufficientData ? (
        <text
          x={centerX}
          y={y - 12}
          textAnchor="middle"
          fill="hsl(var(--muted-foreground))"
          fontSize={14}
        >
          {value}
        </text>
      ) : (
        <>
          <text
            x={centerX - 12}
            y={y - 12}
            textAnchor="middle"
            fill="hsl(var(--foreground))"
            fontSize={24}
            fontWeight={600}
          >
            {value}
          </text>
          <Star
            x={centerX + 8}
            y={y - 28}
            width={20}
            height={20}
            fill="hsl(var(--primary))"
            stroke="none"
          />
        </>
      )}
    </g>
  )
}

function CustomXAxisTick({
  x,
  y,
  payload,
  data,
}: {
  x: number
  y: number
  payload: { value: string }
  data: CustomerGroupData[]
}) {
  const item = data.find((d) => d.name === payload.value)
  if (!item) return null

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={16}
        textAnchor="middle"
        fill={
          item.hasInsufficientData
            ? "hsl(var(--muted-foreground))"
            : "hsl(var(--foreground))"
        }
        fontWeight={700}
        fontSize={12}
      >
        {item.label}
      </text>
      <text
        x={0}
        y={0}
        dy={32}
        textAnchor="middle"
        fill="hsl(var(--muted-foreground))"
        fontSize={11}
      >
        {item.sublabel}
      </text>
    </g>
  )
}

export function LongTermSatisfactionSection({
  customerGroupData,
  companyName,
}: {
  customerGroupData: CustomerGroupData[] | null
  companyName: string
}) {
  if (!customerGroupData) return null

  const data = customerGroupData
  const insight = getLongTermInsight(data, companyName)

  return (
    <div className="space-y-4">
      {insight && (
        <p className="font-serif">
          {insight.parts.map((part, i) =>
            part.shouldHighlight ? (
              <span key={i} className="text-red-700">
                {part.text}
              </span>
            ) : (
              part.text
            )
          )}
        </p>
      )}
      <div className="h-72 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} barCategoryGap="30%" margin={{ top: 40 }}>
            <XAxis
              dataKey="name"
              tickLine={false}
              axisLine={false}
              tick={(props) => <CustomXAxisTick {...props} data={data} />}
              height={50}
            />
            <YAxis domain={[0, 5]} hide width={0} />
            <Bar
              dataKey="rating"
              maxBarSize={120}
              background={{ fill: "hsl(var(--muted))" }}
            >
              {data.map((entry) => (
                <Cell
                  key={entry.name}
                  fill={
                    entry.hasInsufficientData
                      ? "hsl(var(--muted))"
                      : "hsl(var(--primary))"
                  }
                />
              ))}
              <LabelList
                dataKey="displayRating"
                position="top"
                content={(props) => <CustomLabel {...props} data={data} />}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <p className="wb-data-muted">
        Long-term reviews carry the most weight in our methodology because they
        are most representative of what you should be paying for: a system that
        will perform for years.
      </p>
    </div>
  )
}
